import type { SearchRequest } from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import { SortOrder } from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import type { QueryAppsFilter } from '../../types';
import type { Paging } from './types';
import type { SortType } from '../tag-apps';
import { mapSortTypeToSortableField } from './utils';
import type { AppBenefitsService } from '../app-benefits';

const TAG_IDS = {
  benefits: 'eab81c12-5916-4ec7-bb5b-a9bdcdfea4af',
  saleApps: '5b2190c5-bb1b-481f-a879-fd95156f3ff0',
} as const;

export async function createSearchRequest({
  filter,
  paging,
  sortTypes,
  appBenefitsService,
  customParams,
}: {
  filter?: QueryAppsFilter;
  paging?: Paging;
  sortTypes?: SortType[];
  appBenefitsService: AppBenefitsService;
  customParams?: Record<string, any>;
}): Promise<SearchRequest | null> {
  const updatedFilter: QueryAppsFilter = { ...filter };

  if (updatedFilter?.tagId === TAG_IDS.benefits) {
    const benefits = await appBenefitsService.get();
    const benefitsAppIds = benefits.map(({ appId }) => appId);
    updatedFilter.includeAppIds = benefitsAppIds;
    delete updatedFilter.excludeAppIds;
    delete updatedFilter.tagId;
  } else if (updatedFilter?.tagId === TAG_IDS.saleApps) {
    updatedFilter.isSale = true;
    delete updatedFilter.tagId;
  }

  // handle case where includeAppIds is empty
  if (updatedFilter?.includeAppIds?.length === 0) {
    return null;
  }

  const reqFilter: SearchRequest['filter'] = {
    $and: [
      ...(updatedFilter?.tagId
        ? [{ tagIds: { $hasSome: [updatedFilter.tagId] } }]
        : []),
      ...(updatedFilter?.isSale
        ? [{ saleType: { $ne: 'SALE_TYPE_UNSPECIFIED' } }]
        : []),
      ...(updatedFilter?.includeAppIds?.length
        ? [{ doc_id: { $in: updatedFilter.includeAppIds } }]
        : []),
      ...(updatedFilter?.excludeAppIds?.length
        ? [{ doc_id: { $nin: updatedFilter.excludeAppIds } }]
        : []),
    ],
  };

  const req: SearchRequest = {
    ...(updatedFilter?.searchTerm
      ? { searchTerm: updatedFilter.searchTerm }
      : {}),
    ...(paging ? { paging } : {}),
    ...(sortTypes
      ? {
          sortBy: sortTypes.map((sortType) => ({
            field: mapSortTypeToSortableField(sortType),
            order: SortOrder.DESC,
            tagId: updatedFilter?.tagId,
          })),
        }
      : {}),
    ...(reqFilter.$and.length > 0
      ? {
          filter: reqFilter,
        }
      : {}),
    ...(customParams ? { customParams } : {}),
  };

  return req;
}
